import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSuffix, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  NgxMatDatepickerActions,
  NgxMatDatepickerApply,
  NgxMatDatepickerInput,
  NgxMatDatetimepicker
} from '@ngxmc/datetime-picker';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { catchError, map, NEVER, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertsService } from '../../services/alerts.service';
import { Alert, AlertFormData, AlertLevel, AlertVisibilityMode } from '../../models/alerts';
import { ALERT_LEVELS, ALERT_LEVEL_META, AlertLevelMeta } from '../../models/alert-level.util';
import { AlertScheduleDateAdapter } from './alert-schedule-date-adapter';
import { ErrorHandlerService } from '../../../../../../utils/cross-xss-script-error-handler-service';

// Mirrors Alert.MessageMaxLength / LinkTextMaxLength / LinkUrlMaxLength on the backend — caught
// client-side before the round trip, but the server remains the authority.
const MESSAGE_MAX_LENGTH = 2000;
const LINK_TEXT_MAX_LENGTH = 200;
const LINK_URL_MAX_LENGTH = 2000;

// This field's own display format — 'dd-MM-yyyy HH:mm', not the app-wide CUSTOM_NGX_DATE_FORMATS
// ('yyyy-MM-DD HH:mm') that every other ngx-mat-datetime-picker in the app uses — provided
// locally (see AlertFormComponent's own `providers`) so it doesn't reformat those other pickers.
// Casing mirrors the app-wide format exactly (DD/MM uppercase, yyyy lowercase, just reordered),
// since that's the casing AlertScheduleDateAdapter.format()'s moment(date).format(...) call is
// already proven to render correctly.
const ALERT_SCHEDULE_DATE_FORMATS = {
  parse: {
    dateInput: 'DD-MM-yyyy HH:mm'
  },
  display: {
    dateInput: 'DD-MM-yyyy HH:mm',
    dateA11yLabel: 'DD-MM-yyyy',
    monthYearLabel: 'MMM yyyy',
    monthYearA11yLabel: 'MMM yyyy'
  }
};

// Duplicate no longer goes through this form — clicking the list's duplicate icon inserts a
// copy directly (see AlertsComponent.duplicateAlert()), so the only remaining target is Edit.
// Null/absent means the form is in its default "new alert" state.
export interface AlertFormTarget {
  alert: Alert;
}

// Always visible on the page (see AlertsComponent), never a dialog — the design shows the
// create/edit form and the alert list stacked on the same page, not a modal over it. Driven
// entirely by the `target` input: null is the default "new alert" state; a target populates the
// form for editing that alert.
@Component({
  selector: 'app-alert-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatIcon,
    AsyncPipe,
    MatFormField,
    MatInput,
    MatSuffix,
    MatButton,
    MatDatepickerToggle,
    NgxMatDatepickerInput,
    NgxMatDatetimepicker,
    NgxMatDatepickerActions,
    NgxMatDatepickerApply
  ],
  templateUrl: './alert-form.component.html',
  styleUrl: './alert-form.component.scss',
  // Scoped to this component only — see alert-schedule-date-adapter.ts for why the app-wide
  // DateAdapter can't just be extended in place (Angular DI keeps only the last provider
  // registered for a token, not a chain of them).
  providers: [
    { provide: DateAdapter, useClass: AlertScheduleDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: ALERT_SCHEDULE_DATE_FORMATS }
  ]
})
export class AlertFormComponent implements OnChanges {
  @Input() target: AlertFormTarget | null = null;
  @Output() saved = new EventEmitter<Alert>();
  @Output() cleared = new EventEmitter<void>();

  readonly levels = ALERT_LEVELS;
  readonly messageMaxLength = MESSAGE_MAX_LENGTH;
  private errorHandler = inject(ErrorHandlerService);

  allAvailableColumns = [
    {
      key: 'message',
      name: 'Message to the user'
    },
    {
      key: 'linkText',
      name: 'Link text'
    },
    {
      key: 'linkUrl',
      name: 'Link URL'
    },
    {
      key: 'showFrom',
      name: 'Show from'
    },
    {
      key: 'showUntil',
      name: 'Show until'
    }
  ];
  alertForm = new FormGroup({
    level: new FormControl<AlertLevel | null>(null),
    message: new FormControl('', { nonNullable: true }),
    linkText: new FormControl('', { nonNullable: true }),
    linkUrl: new FormControl('', { nonNullable: true }),
    visibilityMode: new FormControl<AlertVisibilityMode>('Scheduled', { nonNullable: true }),
    showFrom: new FormControl('', { nonNullable: true }),
    showUntil: new FormControl('', { nonNullable: true }),
    isActive: new FormControl(true, { nonNullable: true })
  });

  submitted = false;
  saving = false;

  // The Show from value the alert being edited was loaded with ('' in "new alert" mode). A
  // Scheduled alert that is already running has a Show from in the past by definition, so the
  // "must not be in the past" rule only applies when the admin actually changes it — otherwise
  // a live alert couldn't be edited (e.g. a message typo) without moving its start.
  private originalShowFrom = '';

  // Floors the Show from/Show until <input type="datetime-local"> pickers so a past date can't
  // be picked from the calendar UI — a snapshot taken once when the form loads (not recomputed
  // per keystroke); scheduleError below re-checks against a fresh "now" at submit time, since a
  // form can be left open past this snapshot.
  readonly minScheduleDateTime = this.toUtcInputValue(new Date().toISOString());

  // Show until's own floor also can't be earlier than whatever Show from currently holds — a
  // getter (not a static field like minScheduleDateTime above) so it re-evaluates as Show from
  // changes. String comparison is safe here: both values are the same fixed-width
  // "YYYY-MM-DDTHH:mm" shape, which sorts identically to a chronological comparison.
  get minShowUntil(): string {
    const showFrom = this.alertForm.controls.showFrom.value;
    return showFrom > this.minScheduleDateTime ? showFrom : this.minScheduleDateTime;
  }

  // The ngx-mat-datetime-picker's own [min] input takes a Date, not the string shape
  // minScheduleDateTime/minShowUntil are kept in for the (unchanged) rest of this component —
  // scheduleStringToDate below converts between the two without any timezone arithmetic.
  // Lowered to the edited alert's own (past) Show from while editing a running alert, so the
  // picker doesn't flag the untouched value invalid — see originalShowFrom.
  get minScheduleDateTimeAsDate(): Date {
    const floor =
      this.originalShowFrom && this.originalShowFrom < this.minScheduleDateTime
        ? this.originalShowFrom
        : this.minScheduleDateTime;
    return this.scheduleStringToDate(floor)!;
  }

  get minShowUntilAsDate(): Date {
    return this.scheduleStringToDate(this.minShowUntil)!;
  }

  // Bound to the pickers via [ngModel]/(ngModelChange) — see onShowFromDateChange/
  // onShowUntilDateChange below for how these stay in sync with the reactive showFrom/showUntil
  // string controls that save()/toUtcIso()/scheduleError already rely on unchanged.
  showFromDate: Date | null = null;
  showUntilDate: Date | null = null;

  // Drives the mobile-only message placeholder copy swap (item 4 of the mobile-correction
  // spec) — the rest of the mobile reflow is plain CSS, but placeholder text is content, not
  // style, so it needs this component to be breakpoint-aware too (see AlertsComponent's own
  // isMobile$ for the same pattern at the page level).
  isMobile$ = inject(BreakpointObserver)
    .observe(Breakpoints.Handset)
    .pipe(map(result => result.matches));

  private alertsService = inject(AlertsService);
  private matSnackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  get isEditMode(): boolean {
    return this.target !== null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!('target' in changes)) {
      return;
    }
    this.submitted = false;
    const alert = this.target?.alert;
    if (alert) {
      const showFromValue = alert.showFrom ? this.toUtcInputValue(alert.showFrom) : '';
      const showUntilValue = alert.showUntil ? this.toUtcInputValue(alert.showUntil) : '';
      this.originalShowFrom = showFromValue;
      this.alertForm.patchValue({
        level: alert.level,
        message: alert.message,
        linkText: alert.linkText ?? '',
        linkUrl: alert.linkUrl ?? '',
        visibilityMode: alert.visibilityMode,
        showFrom: showFromValue,
        showUntil: showUntilValue,
        isActive: alert.isActive
      });
      this.showFromDate = this.scheduleStringToDate(showFromValue);
      this.showUntilDate = this.scheduleStringToDate(showUntilValue);
    } else {
      this.resetForm();
    }
  }

  // Keeps the picker's own Date-typed model in sync with the reactive showFrom/showUntil string
  // controls — save()/toUtcIso()/scheduleError all read those controls unchanged, so this is the
  // only place the new picker touches them.
  onShowFromDateChange(date: Date | null) {
    this.showFromDate = date;
    this.alertForm.controls.showFrom.setValue(date ? this.dateToScheduleString(date) : '');
  }

  onShowUntilDateChange(date: Date | null) {
    this.showUntilDate = date;
    this.alertForm.controls.showUntil.setValue(date ? this.dateToScheduleString(date) : '');
  }

  selectLevel(level: AlertLevel) {
    this.alertForm.controls.level.setValue(level);
  }

  levelMeta(level: AlertLevel): AlertLevelMeta {
    return ALERT_LEVEL_META[level];
  }

  selectVisibilityMode(mode: AlertVisibilityMode) {
    this.alertForm.controls.visibilityMode.setValue(mode);
  }

  get levelError(): string | null {
    return this.submitted && !this.alertForm.controls.level.value ? 'Select an alert level' : null;
  }

  get messageError(): string | null {
    if (!this.submitted) {
      return null;
    }
    const message = this.alertForm.controls.message.value.trim();
    if (!message) {
      return 'Message is required';
    }
    if (message.length > MESSAGE_MAX_LENGTH) {
      return `Message must be at most ${MESSAGE_MAX_LENGTH} characters`;
    }
    return null;
  }

  // Mirrors AlertValidationRules.ValidateLink on the backend: both-or-neither, http(s) only.
  get linkError(): string | null {
    if (!this.submitted) {
      return null;
    }
    const { linkText, linkUrl } = this.alertForm.getRawValue();
    const hasText = linkText.trim().length > 0;
    const hasUrl = linkUrl.trim().length > 0;

    if (hasText !== hasUrl) {
      return 'Link text and link URL must both be provided, or both left empty';
    }
    if (!hasUrl) {
      return null;
    }
    if (linkText.trim().length > LINK_TEXT_MAX_LENGTH) {
      return `Link text must be at most ${LINK_TEXT_MAX_LENGTH} characters`;
    }
    if (linkUrl.trim().length > LINK_URL_MAX_LENGTH) {
      return `Link URL must be at most ${LINK_URL_MAX_LENGTH} characters`;
    }
    if (!/^https?:\/\//i.test(linkUrl.trim())) {
      return 'Link URL must start with http:// or https://';
    }
    return null;
  }

  // Mirrors AlertValidationRules.ValidateSchedule on the backend.
  get scheduleError(): string | null {
    if (!this.submitted || this.alertForm.controls.visibilityMode.value !== 'Scheduled') {
      return null;
    }
    const { showFrom, showUntil } = this.alertForm.getRawValue();
    if (!showFrom || !showUntil) {
      return 'Scheduled alerts require both a show-from and a show-until date';
    }
    const from = new Date(`${showFrom}Z`);
    const until = new Date(`${showUntil}Z`);
    // Checked against a fresh "now", not minScheduleDateTime's load-time snapshot — the [min]
    // attribute on the pickers only blocks picking a past date from the calendar UI, not typing
    // or pasting one in directly, and a form left open long enough can make even a
    // once-valid-future date have since slipped into the past. Skipped for an edit that leaves
    // Show from unchanged (see originalShowFrom).
    const showFromChanged = !this.isEditMode || showFrom !== this.originalShowFrom;
    if (showFromChanged && from < new Date()) {
      return 'Show from must not be in the past';
    }
    if (until <= from) {
      return 'Show until must be after show from';
    }
    return null;
  }

  save() {
    this.submitted = true;
    if (this.levelError || this.messageError || this.linkError || this.scheduleError) {
      return;
    }

    const form = this.alertForm.getRawValue();
    const isScheduled = form.visibilityMode === 'Scheduled';
    const data: AlertFormData = {
      level: form.level!,
      message: form.message.trim(),
      linkText: this.orNull(form.linkText),
      linkUrl: this.orNull(form.linkUrl),
      visibilityMode: form.visibilityMode,
      showFrom: isScheduled ? this.toUtcIso(form.showFrom) : null,
      showUntil: isScheduled ? this.toUtcIso(form.showUntil) : null,
      isActive: form.isActive
    };

    const isEditMode = this.isEditMode;
    this.saving = true;
    const request$ = isEditMode
      ? this.alertsService.update(this.target!.alert.id, data)
      : this.alertsService.create(data);

    request$
      .pipe(
        tap(alert => {
          this.matSnackBar.open(isEditMode ? 'Alert updated' : 'Alert created', 'x', { panelClass: 'snackbar-green' });
          this.saving = false;
          // Reset directly rather than relying on the parent setting `target` back to null —
          // for a brand-new alert `target` is already null before this save, so setting it to
          // null again afterward doesn't change the @Input()'s reference and Angular never fires
          // ngOnChanges, meaning resetForm() would never run at all. Only an edit (target:
          // non-null -> null) used to trigger it that way; create silently never cleared.
          this.resetForm();
          this.saved.emit(alert);
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.saving = false;

          this.errorHandler.showApiError(
            error,
            this.errorMessageFor(error, isEditMode),
            this.allAvailableColumns,
            (fieldName, options) => options.find(x => x.key === fieldName)?.name,
            true
          );
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  clear() {
    this.resetForm();
    this.cleared.emit();
  }

  private resetForm() {
    this.submitted = false;
    this.originalShowFrom = '';
    // A new alert starts with no schedule opinion at all — blank fields showing the input's own
    // empty placeholder, not a pre-filled "now" that would read as the app silently picking a
    // schedule the admin never chose.
    this.showFromDate = null;
    this.showUntilDate = null;
    this.alertForm.reset({
      level: null,
      message: '',
      linkText: '',
      linkUrl: '',
      visibilityMode: 'Scheduled',
      showFrom: '',
      showUntil: '',
      isActive: true
    });
  }

  private orNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  // STRICT LITERAL UTC RULE — do not touch any of the methods below without re-reading this
  // comment. The field's hint ("All times are in UTC") means the digits in the box ARE the UTC
  // value, verbatim, in both directions. `new Date("YYYY-MM-DDTHH:mm")` (a bare string, no
  // 'Z'/offset) is parsed by JS as LOCAL time, and pulling UTC components back out of that (or
  // calling .toISOString()) silently shifts the value by the browser's own UTC offset — this
  // exact bug shipped twice already (a "Today"-click normalizer, and later a (change)+(input)
  // double-fire of it) each losing/gaining hours, and once a whole calendar day. The two safe
  // patterns used here are: (1) `new Date(iso)` where `iso` already carries a real 'Z'/offset
  // (toUtcInputValue, decoding a value that came FROM the backend) or a manually appended one
  // (toUtcIso, going TO the backend) — never a bare picked/typed string; (2) the multi-argument
  // `new Date(year, month, day, hours, minutes)` constructor (dateToScheduleString/
  // scheduleStringToDate, bridging the ngx-mat-datetime-picker's Date-typed model), which builds
  // a LOCAL date directly from components with no string parsing and therefore no timezone
  // inference at all — under AlertScheduleDateAdapter (see alert-schedule-date-adapter.ts) that
  // Date's LOCAL fields are exactly the picker's own displayed/picked digits.

  // Renders an ISO (UTC) timestamp — a real absolute instant from the backend — into a
  // <input type="datetime-local"> value using its UTC components. Safe because the input `iso`
  // already carries a 'Z'/offset, so `new Date(iso)` parses it as the correct absolute instant
  // before UTC components are read back out.
  private toUtcInputValue(iso: string): string {
    const date = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  }

  // A <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm") carries no time zone of its own —
  // appending 'Z' is what tells Date to treat those digits as UTC literally, instead of parsing
  // them as the browser's local time zone (Date's default for an offset-less string). No other
  // arithmetic happens here: whatever digits the admin typed/picked are exactly what gets sent.
  private toUtcIso(value: string): string {
    return new Date(`${value}Z`).toISOString();
  }

  // Reads a Date's LOCAL fields — which under AlertScheduleDateAdapter are exactly the digits
  // the picker displayed/picked — into the same 'YYYY-MM-DDTHH:mm' shape the showFrom/showUntil
  // controls already use.
  private dateToScheduleString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  // Reverse of dateToScheduleString — builds a Date via the multi-argument constructor (see the
  // STRICT LITERAL UTC RULE comment above), never `new Date(string)`, so no timezone inference
  // happens: the digits in `value` become the Date's LOCAL fields directly.
  private scheduleStringToDate(value: string): Date | null {
    if (!value) {
      return null;
    }
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }

  private errorMessageFor(error: HttpErrorResponse, isEditMode: boolean): string {
    const fieldErrors: unknown = error.error?.errors;
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      return fieldErrors.map((entry: { message?: string }) => entry.message).join(' ');
    }
    return `Could not ${isEditMode ? 'update' : 'create'} the alert. Please try again.`;
  }
}
